/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isObjectLike from 'lodash/isObjectLike';
import isString from 'lodash/isString';

import { isISOString, isLessOrEqual } from '../iso-string';
import { isServerDeviceId } from '../session/session-attrs';

/**
 * @import { ISOString } from '../iso-string'
 * @import { ServerDeviceId } from '../session/session-attrs'
 */

// Názvy `opened` a `closed` jsou zde z historických důvodů, kde ještě existovala
// implementace analytického cyklu, kde po uplynutí určité doby se aktualizoval -
// **otevřela** se nová skupina analytických dat a **uzavřela** se stará.
//
// Správně by se mělo jednat o `start` a `end`, což je i sémanticky správnější,
// tj. totéž pojmenování jako ve formátu odesílaném na Ki-Wi Server.

/**
 * Atributy skupiny analytických dat pro verzi 1.
 *
 * Tu je předpokladáno, že všechny atributy jsou povinné, až na `closed`, který
 * může být null. To bylo z důvodu, že v původní implementaci při otevření nové
 * skupiny se nastavil jenom atribut `opened` na aktuálni čas a `closed === null`
 * implikovalo, že skupina je otevřená (probíhá). Až po uplynutí periody se
 * nastavil `closed` na aktuální čas.
 * @typedef AnalyticsGroupV1Attrs
 * @property {string} id Identifikátor.
 * @property {ISOString} opened Začátek záznamu skupiny dat.
 * @property {ISOString | null} closed Konec záznamu skupiny dat nebo `null`
 * pro neuzavřenou skupinu.
 */

/**
 * Atributy skupiny analytických dat v aktuální verzi 2.
 * @typedef AnalyticsGroupAttrs
 * @property {string} id Identifikátor.
 * @property {ISOString} opened Začátek záznamu skupiny dat.
 * @property {ISOString} closed Konec záznamu skupiny dat.
 * @property {ServerDeviceId['serverUrl']} serverUrl
 * @property {ServerDeviceId['deviceId']} deviceId
 */

/**
 * Atributy skupiny analytických dat v1 nebo v2.
 * @typedef {AnalyticsGroupAttrs | AnalyticsGroupV1Attrs} AnalyticsGroupVxAttrs
 */

/**
 * TODO #555: UnionToIntersection<AnalyticsGroupVxAttrs>
 * @typedef {{
 *   id: string;
 *   opened: ISOString;
 *   closed: ISOString | null;
 *   serverUrl?: ServerDeviceId['serverUrl'];
 *   deviceId?: ServerDeviceId['deviceId'];
 * }} MaybeAnalyticsGroupVxAttrs
 */

/**
 * Zjistí, zda je hodnota instancí {@link AnalyticsGroupVxAttrs}.
 * @param {MaybeAnalyticsGroupVxAttrs} attrs
 * @returns {attrs is AnalyticsGroupVxAttrs}
 */
export function isAnalyticsGroupVxAttrs(attrs) {
  // Pokud to není baseAnalyticsGroupAttrs, tak je to určitě nevalidní.
  if (
    !(
      isObjectLike(attrs) &&
      isString(attrs.id) &&
      isISOString(attrs.opened) &&
      (attrs.closed === null ||
        (isISOString(attrs.closed) &&
          isLessOrEqual(attrs.opened, attrs.closed)))
    )
  ) {
    return false;
  }

  // Pokud to má být v1, tak nesmí mít nastavené serverUrl a deviceId.
  if (isAnalyticsGroupVxV1Attrs(attrs)) {
    return true;
  }

  // Musí to validní v2, jinak je to nevalidní.
  return isAnalyticsGroupVxV2Attrs(attrs);
}

/**
 * Zjistí, zda je {@link AnalyticsGroupVxAttrs} instancí {@link AnalyticsGroupV1Attrs}.
 *
 * TODO #389: Musí se volat vždy po {@link isAnalyticsGroupVxAttrs}, které prověří
 * i vztahy opened/closed, i když typově to přijme i {@link MaybeAnalyticsGroupVxAttrs}.
 * @param {AnalyticsGroupVxAttrs} attrs
 * @returns {attrs is AnalyticsGroupV1Attrs}
 */
export function isAnalyticsGroupVxV1Attrs(attrs) {
  // Pokud to má být v1, tak nesmí mít nastavené serverUrl a deviceId.
  // TODO #555: UnionToIntersection
  const maybe = /** @type {MaybeAnalyticsGroupVxAttrs} */ (attrs);
  return maybe.serverUrl === undefined && maybe.deviceId === undefined;
}

/**
 * Zjistí, zda je {@link AnalyticsGroupVxAttrs} instancí {@link AnalyticsGroupAttrs}.
 *
 * TODO #389: Musí se volat vždy po {@link isAnalyticsGroupVxAttrs}, které prověří
 * i vztahy opened/closed, i když typově to přijme i {@link MaybeAnalyticsGroupVxAttrs}.
 * @param {AnalyticsGroupVxAttrs} attrs
 * @returns {attrs is AnalyticsGroupAttrs}
 */
export function isAnalyticsGroupVxV2Attrs(attrs) {
  // Pokud to má být v2, tak to musí být/mýt ServerDeviceId a taky nesmí být
  // closed null (isBaseAnalyticsGroupAttrs zajišťuje, že když to není null,
  // tak je to větší nebo rovno opened).
  // TODO #555: UnionToIntersection
  const maybe = /** @type {MaybeAnalyticsGroupVxAttrs} */ (attrs);
  return isServerDeviceId(maybe) && maybe.closed !== null;
}
